import React from "react"
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table"
import { useFilters, useGlobalFilter, useSortBy, useTable } from "react-table"
import { ChangeHealthcareLog } from "../../types/ChangeHealthcareLog"
import { Box, Center } from "@chakra-ui/layout"
import { convertFromUtcToLocal } from "../../utils/convertFromUtcToLocal"
import DateRangeColumnFilter, { dateBetweenFilter } from "./tablefilters/DateRangeColumnFilter"
import DefaultColumnFilter from "./tablefilters/DefaultColumnFilter"
import GlobalFilter from "./tablefilters/GlobalFilter"
import FileViewerModalButton from "./FileViewerModalButton"
import SelectColumnFilterSucceeded from "./tablefilters/SelectColumnFilterSucceeded"
import SelectColumnFilterMemberPartner from "./tablefilters/SelectColumnFilterMemberPartner"
import SelectColumnFilter from "./tablefilters/SelectColumnFilter"

function ChangeHealthLogsTableChild({ columns, data }) {
  const filterTypes = React.useMemo(
    () => ({
      dateBetween: dateBetweenFilter,
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true
        })
      }
    }),
    []
  )

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: {
        sortBy: [
          {
            id: "timestamp",
            desc: true
          }
        ]
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  return (
    <>
      <Box
        width="100vw"
        position="relative"
        left="50%"
        right="50%"
        marginLeft="-50vw"
        marginRight="-50vw"
        overflowX="scroll"
      >
        <Table {...getTableProps()} size="sm">
          <Thead>
            {headerGroups.map((headerGroup, i) => (
              <Tr key={i} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, j) => (
                  <Th key={j}>
                    <div {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render("Header")}
                      <span>
                        {!column.isSorted ? (
                          ""
                        ) : column.isSortedDesc ? (
                          <ChevronDownIcon boxSize={6} />
                        ) : (
                          <ChevronUpIcon boxSize={6} />
                        )}
                      </span>
                    </div>
                    <Box marginTop={2}>{column.canFilter ? column.render("Filter") : null}</Box>
                  </Th>
                ))}
              </Tr>
            ))}
            <Tr>
              <Th
                colSpan={visibleColumns.length}
                style={{
                  textAlign: "left"
                }}
              >
                <GlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={state.globalFilter}
                  setGlobalFilter={setGlobalFilter}
                />
              </Th>
            </Tr>
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row)
              return (
                <Tr key={i} {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <Td key={i} {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </Td>
                    )
                  })}
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  )
}

type ChangeHealthcareLogsTableProps = {
  logs: Array<ChangeHealthcareLog>
}

export default function ChangeHealthcareLogsTable({ logs }: ChangeHealthcareLogsTableProps): JSX.Element {
  const columns = React.useMemo(
    () => [
      {
        Header: "Change Healthcare Request Logs",
        columns: [
          {
            Header: "Timestamp",
            accessor: "timestamp",
            Filter: DateRangeColumnFilter,
            filter: "dateBetween",
            // eslint-disable-next-line react/display-name
            Cell: (props) => <div>{convertFromUtcToLocal(props.value)}</div>
          },
          {
            Header: "Details",
            accessor: "responseJsonAvailable",
            disableFilters: true,
            disableSortBy: true,
            // eslint-disable-next-line react/display-name
            Cell: (props) => (
              <div>
                {props.value ? (
                  <div>
                    <FileViewerModalButton title={"Request"} id={props.row.original.id} />
                    <FileViewerModalButton title={"Response"} id={props.row.original.id} />
                  </div>
                ) : (
                  <div>
                    <FileViewerModalButton title={"Request"} id={props.row.original.id} />
                    <Box fontSize="12px" m={1}>
                      No Response
                    </Box>
                  </div>
                )}
              </div>
            )
          },
          {
            Header: "Succeeded",
            accessor: "succeeded",
            Filter: SelectColumnFilterSucceeded,
            filter: "equals",
            // eslint-disable-next-line react/display-name
            Cell: (props) => <div>{props.value ? "Yes" : "FAILED"}</div>
          },
          {
            Header: "Employee ID",
            accessor: "employeeId"
          },
          {
            Header: "Company ID",
            accessor: "companyId"
          },
          {
            Header: "Exception Message",
            accessor: "exceptionMessage"
          },
          {
            Header: "Trading Partner ID",
            accessor: "tradingPartnerId",
            Filter: SelectColumnFilter,
            filter: "includes"
          },
          {
            Header: "Member ID",
            accessor: "memberId"
          },
          {
            Header: "Who was the request for",
            accessor: "wasForMember",
            Filter: SelectColumnFilterMemberPartner,
            filter: "equals",
            // eslint-disable-next-line react/display-name
            Cell: (props) => <div>{props.value ? "Member" : "Partner"}</div>
          },
          {
            Header: "Control Number",
            accessor: "controlNumber"
          }
        ]
      }
    ],
    []
  )

  if (!logs || logs.length === 0) return <Center>No Results</Center>

  return <ChangeHealthLogsTableChild columns={columns} data={logs} />
}
